/**
 * User Service - Production Ready
 * Backend API: /web/master/user
 *
 * Complete user management system supporting:
 * - Backend users (admin, managers, staff)
 * - Sales users (field force with hierarchy)
 * - User hierarchy management
 * - Team management
 * - Bulk operations
 *
 * @version 2.0.0 - Cleaned up duplicate methods, standardized API endpoints
 */

import apiService from 'core/services/apiClient';
import logger from 'core/utils/logger';
import type { User, UserFormData, UserListFilters, UserStats } from '../types';

/**
 * Base URL for user management APIs
 * Note: API versioning is handled by apiClient interceptor
 */
const BASE_URL = '/web/master/user';

class UserService {
  // ==================== BACKEND USER OPERATIONS ====================

  /**
   * Get backend users list with filters and pagination
   */
  async getBackendUserList(
    filters: UserListFilters = {},
    page: number = 1,
    limit: number = 10
  ): Promise<{ data: User[]; total: number }> {
    try {
      logger.info('Fetching backend user list', { filters, page, limit });

      const params = {
        ...filters,
        page,
        limit,
      };

      const response = await apiService.get(`${BASE_URL}/backend-users`, { params });

      // Backend response structure:
      // {
      //   statusCode: 200,
      //   status: 'success',
      //   message: 'Users retrieved successfully',
      //   data: [...],
      //   pagination: { page, limit, total, totalPages, hasNextPage, ... },
      //   meta: { timestamp, version }
      // }

      return {
        data: response.data || [],
        total: response.pagination.total || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch backend user list', error);
      throw error;
    }
  }

  /**
   * Get backend user detail by ID
   */
  async getBackendUserDetail(userId: string | number): Promise<User> {
    try {
      logger.info('Fetching backend user detail', { userId });

      const response = await apiService.get(`${BASE_URL}/backend-users/${userId}`);

      const data = response?.data || response;
      return data?.user || data;
    } catch (error) {
      logger.error('Failed to fetch backend user detail', error);
      throw error;
    }
  }

  /**
   * Create new backend user
   */
  async createBackendUser(userData: UserFormData): Promise<any> {
    try {
      logger.info('Creating backend user', { userData });

      const response = await apiService.post(`${BASE_URL}/backend-users`, userData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create backend user', error);
      throw error;
    }
  }

  /**
   * Update existing backend user
   */
  async updateBackendUser(userId: string | number, userData: UserFormData): Promise<any> {
    try {
      logger.info('Updating backend user', { userId });

      const response = await apiService.put(`${BASE_URL}/backend-users/${userId}`, userData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update backend user', error);
      throw error;
    }
  }

  /**
   * Delete backend user (soft delete)
   */
  async deleteBackendUser(userId: string | number): Promise<any> {
    try {
      logger.info('Deleting backend user', { userId });

      const response = await apiService.delete(`${BASE_URL}/backend-users/${userId}`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to delete backend user', error);
      throw error;
    }
  }

  // ==================== SALES USER OPERATIONS ====================

  /**
   * Get sales users list with filters and pagination
   */
  async getSalesUserList(
    filters: UserListFilters = {},
    page: number = 1,
    limit: number = 10
  ): Promise<{ data: User[]; total: number }> {
    try {
      logger.info('Fetching sales user list', { filters, page, limit });

      const params = {
        ...filters,
        page,
        limit,
      };

      const response = await apiService.get(`${BASE_URL}/sales-users`, { params });

      // Backend response structure:
      // {
      //   statusCode: 200,
      //   status: 'success',
      //   message: 'Sales users retrieved successfully',
      //   data: [...],
      //   pagination: { page, limit, total, totalPages, hasNextPage, ... },
      //   meta: { timestamp, version }
      // }

      return {
        data: response.data || [],
        total: response.pagination.total || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch sales user list', error);
      throw error;
    }
  }

  /**
   * Get sales user detail by ID with full hierarchy
   */
  async getSalesUserDetail(userId: string | number): Promise<User> {
    try {
      logger.info('Fetching sales user detail', { userId });

      const response = await apiService.get(`${BASE_URL}/sales-users/${userId}`);

      const data = response?.data || response;
      return data?.user || data;
    } catch (error) {
      logger.error('Failed to fetch sales user detail', error);
      throw error;
    }
  }

  /**
   * Create new sales user (field force)
   */
  async createSalesUser(userData: UserFormData): Promise<any> {
    try {
      logger.info('Creating sales user', { userData });

      const response = await apiService.post(`${BASE_URL}/sales-users`, userData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create sales user', error);
      throw error;
    }
  }

  /**
   * Update existing sales user
   */
  async updateSalesUser(userId: string | number, userData: UserFormData): Promise<any> {
    try {
      logger.info('Updating sales user', { userId });

      const response = await apiService.put(`${BASE_URL}/sales-users/${userId}`, userData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update sales user', error);
      throw error;
    }
  }

  /**
   * Delete sales user (with subordinate reassignment)
   */
  async deleteSalesUser(userId: string | number): Promise<any> {
    try {
      logger.info('Deleting sales user', { userId });

      const response = await apiService.delete(`${BASE_URL}/sales-users/${userId}`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to delete sales user', error);
      throw error;
    }
  }


  /**
   * Update sales user base location (lat/lng)
   */
  async updateSalesUserLocation(
    userId: string | number,
    locationData: { baseLat: number; baseLng: number }
  ): Promise<any> {
    try {
      logger.info('Updating sales user location', { userId, locationData });

      const response = await apiService.patch(`${BASE_URL}/sales-users/${userId}/location`, locationData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update sales user location', error);
      throw error;
    }
  }

  // ==================== COMMON USER OPERATIONS ====================

  /**
   * Change user status (Active/Inactive/Suspended)
   */
  async changeUserStatus(
    userId: string | number,
    status: string,
    reason?: string
  ): Promise<any> {
    try {
      logger.info('Changing user status', { userId, status, reason });

      // Backend expects status enum: 'active' | 'inactive' | 'suspended'
      const normalizedStatus = status.toLowerCase();
      const response = await apiService.patch(`${BASE_URL}/users/${userId}/status`, {
        status: normalizedStatus,
        reason,
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to change user status', error);
      throw error;
    }
  }

  /**
   * Change user password
   */
  async changePassword(
    userId: string | number,
    currentPassword: string,
    newPassword: string,
    confirmPassword?: string
  ): Promise<any> {
    try {
      logger.info('Changing user password', { userId });

      const response = await apiService.post(`${BASE_URL}/users/${userId}/change-password`, {
        currentPassword,
        newPassword,
        confirmPassword: confirmPassword || newPassword,
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to change password', error);
      throw error;
    }
  }

  /**
   * Send forgot password email
   */
  async forgotPassword(email: string): Promise<any> {
    try {
      logger.info('Sending forgot password email', { email });

      const response = await apiService.post(`${BASE_URL}/users/forgot-password`, {
        email,
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to send forgot password email', error);
      throw error;
    }
  }

  // ==================== IMPORT/EXPORT OPERATIONS (Background Jobs) ====================

  /**
   * Download import template for user import
   * Returns an Excel file with proper headers and sample data
   */
  async downloadImportTemplate(userType: 'backend' | 'sales'): Promise<void> {
    try {
      logger.info('Downloading import template', { userType });

      const response = await apiService.get(`${BASE_URL}/import-export/template/${userType}`, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${userType}_users_import_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Failed to download import template', error);
      throw error;
    }
  }

  /**
   * Start user import job (Background processing with email notification)
   *
   * Flow:
   * 1. Upload file -> API validates user email exists
   * 2. Job queued -> Returns jobId immediately
   * 3. Background processing -> Email sent on completion with report
   */
  async startImportJob(params: {
    fileContent: string;  // Base64 encoded file
    fileName: string;
    importType: 'backend_user' | 'sales_user';
    updateExisting?: boolean;
    skipDuplicates?: boolean;
    jobName?: string;
  }): Promise<{ jobId: string; message: string; totalRows?: number }> {
    try {
      logger.info('Starting user import job', { fileName: params.fileName, importType: params.importType });

      const response = await apiService.post(`${BASE_URL}/import-export/import`, params);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to start import job', error);
      throw error;
    }
  }

  /**
   * Start user export job (Background processing with email notification)
   *
   * Flow:
   * 1. Request export -> API validates user email exists
   * 2. Job queued -> Returns jobId immediately
   * 3. Background processing -> Email sent with Excel file attachment
   */
  async startExportJob(params: {
    exportType: 'all_users' | 'backend_users' | 'sales_users';
    format?: 'excel' | 'csv';
    filters?: {
      status?: string;
      department?: string;
      designation?: string;
      createdFrom?: string;
      createdTo?: string;
    };
    selectedFields?: string[];
    jobName?: string;
  }): Promise<{ jobId: string; message: string }> {
    try {
      logger.info('Starting user export job', { exportType: params.exportType });

      const response = await apiService.post(`${BASE_URL}/import-export/export`, params);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to start export job', error);
      throw error;
    }
  }

  /**
   * Get import/export job history
   */
  async getImportExportJobs(params?: {
    type?: 'import' | 'export';
    status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ jobs: any[]; total: number }> {
    try {
      logger.info('Fetching import/export jobs', params);

      const response = await apiService.get(`${BASE_URL}/import-export/jobs`, { params });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch import/export jobs', error);
      throw error;
    }
  }

  /**
   * Alias for getImportExportJobs - used by BackgroundJobsDropdown
   */
  async getJobHistory(params?: {
    type?: 'import' | 'export';
    status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ jobs: any[]; total: number }> {
    return this.getImportExportJobs(params);
  }

  /**
   * Get single job status
   */
  async getJobStatus(jobId: string): Promise<any> {
    try {
      logger.info('Fetching job status', { jobId });

      const response = await apiService.get(`${BASE_URL}/import-export/jobs/${jobId}`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch job status', error);
      throw error;
    }
  }

  /**
   * Cancel a pending/processing job
   */
  async cancelJob(jobId: string): Promise<{ success: boolean }> {
    try {
      logger.info('Cancelling job', { jobId });

      const response = await apiService.post(`${BASE_URL}/import-export/jobs/${jobId}/cancel`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to cancel job', error);
      throw error;
    }
  }

  // ==================== HIERARCHY MANAGEMENT ====================

  /**
   * Manage user hierarchy (assign/change reporting manager)
   */
  async manageHierarchy(
    userId: string,
    reportingManagerId: string,
    effectiveFrom?: string
  ): Promise<any> {
    try {
      logger.info('Managing user hierarchy', { userId, reportingManagerId, effectiveFrom });

      const response = await apiService.post(`${BASE_URL}/hierarchy/manage`, {
        userId,
        reportingManagerId,
        effectiveFrom: effectiveFrom || new Date().toISOString(),
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to manage hierarchy', error);
      throw error;
    }
  }

  /**
   * Get complete hierarchy tree
   */
  async getHierarchyTree(rootUserId?: string): Promise<any> {
    try {
      logger.info('Fetching hierarchy tree', { rootUserId });

      const params = rootUserId ? { rootUserId } : {};
      const response = await apiService.get(`${BASE_URL}/hierarchy/tree`, { params });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch hierarchy tree', error);
      throw error;
    }
  }

  /**
   * Get reporting manager of user
   */
  async getReportingManager(userId: string): Promise<any> {
    try {
      logger.info('Fetching reporting manager', { userId });

      const response = await apiService.get(`${BASE_URL}/hierarchy/${userId}/reporting-manager`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch reporting manager', error);
      throw error;
    }
  }

  /**
   * Get all juniors (recursive - all levels)
   */
  async getAllJuniors(userId: string): Promise<any[]> {
    try {
      logger.info('Fetching all juniors', { userId });

      const response = await apiService.get(`${BASE_URL}/hierarchy/${userId}/juniors`);

      const data = response?.data || response;
      return Array.isArray(data) ? data : (data?.list || data?.juniors || []);
    } catch (error) {
      logger.error('Failed to fetch all juniors', error);
      return [];
    }
  }

  /**
   * Get juniors hierarchy for sales user (with nested structure)
   */
  async getJuniorsHierarchy(userId: string): Promise<{
    data: any[];
    total_juniors: number;
    direct_juniors: number;
  }> {
    try {
      logger.info('Fetching juniors hierarchy', { userId });

      const response = await apiService.get(`${BASE_URL}/sales-users/${userId}/juniors-hierarchy`);

      // Response structure after apiClient unwrapping:
      // { data: [...juniors...], total_juniors: 7, direct_juniors: 3 }
      // Handle both array response and object response with nested data
      if (Array.isArray(response)) {
        // Direct array response
        return {
          data: response,
          total_juniors: response.length,
          direct_juniors: response.length,
        };
      }

      // Object response with data property
      return {
        data: Array.isArray(response?.data) ? response.data : [],
        total_juniors: response?.total_juniors || 0,
        direct_juniors: response?.direct_juniors || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch juniors hierarchy', error);
      return { data: [], total_juniors: 0, direct_juniors: 0 };
    }
  }

  /**
   * Get assigned users for backend user (for USER-wise data access)
   * Returns hierarchical structure with juniors for each assigned user
   */
  async getBackendUserAssignedUsers(userId: string): Promise<{
    data: any[];
    total: number;
    total_juniors: number;
    direct: number;
  }> {
    try {
      logger.info('Fetching assigned users for backend user', { userId });

      const response = await apiService.get(`${BASE_URL}/backend-users/${userId}/assigned-users`);

      // Response structure after apiClient unwrapping:
      // { data: [...users...], total: 3, total_juniors: 4, direct: 3 }
      // Handle both array response and object response with nested data
      if (Array.isArray(response)) {
        // Direct array response
        return {
          data: response,
          total: response.length,
          total_juniors: response.length,
          direct: response.length,
        };
      }

      // Object response with data property
      return {
        data: Array.isArray(response?.data) ? response.data : [],
        total: response?.total || 0,
        total_juniors: response?.total_juniors || 0,
        direct: response?.direct || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch assigned users', error);
      return { data: [], total: 0, total_juniors: 0, direct: 0 };
    }
  }

  /**
   * Get direct juniors only (immediate subordinates)
   */
  async getDirectJuniors(userId: string): Promise<any[]> {
    try {
      logger.info('Fetching direct juniors', { userId });

      const response = await apiService.get(`${BASE_URL}/hierarchy/${userId}/direct-juniors`);

      const data = response?.data || response;
      return Array.isArray(data) ? data : (data?.list || data?.juniors || []);
    } catch (error) {
      logger.error('Failed to fetch direct juniors', error);
      return [];
    }
  }

  /**
   * Update user credentials (username and/or password)
   * Separate API for credential management from UserDetail page
   */
  async updateUserCredentials(
    userId: string,
    credentials: {
      username?: string;
      newPassword?: string;
      confirmPassword?: string;
    }
  ): Promise<{ success: boolean; username?: string; visible_password?: string; passwordUpdated?: boolean }> {
    try {
      logger.info('Updating user credentials', { userId, hasUsername: !!credentials.username, hasPassword: !!credentials.newPassword });

      const response = await apiService.patch(`${BASE_URL}/users/${userId}/credentials`, credentials);

      const data = response?.data || response;
      return {
        success: true,
        username: data?.username,
        visible_password: data?.visible_password,
        passwordUpdated: data?.passwordUpdated,
      };
    } catch (error) {
      logger.error('Failed to update user credentials', error);
      throw error;
    }
  }

  // ==================== DEVICE LOCK MANAGEMENT ====================

  /**
   * Reset device lock for a user (admin action)
   * Allows user to login from a new device
   */
  async resetDeviceLock(userId: string): Promise<{ success: boolean; wasLocked: boolean; previousDevice?: string }> {
    try {
      logger.info('Resetting device lock', { userId });
      const response = await apiService.patch(`${BASE_URL}/users/${userId}/reset-device`);
      const data = response?.data || response;
      return {
        success: true,
        wasLocked: data?.wasLocked ?? false,
        previousDevice: data?.previousDevice,
      };
    } catch (error) {
      logger.error('Failed to reset device lock', error);
      throw error;
    }
  }

  // ==================== TEAM MANAGEMENT ====================

  /**
   * Create new team
   */
  async createTeam(teamData: {
    teamName: string;
    teamLeadId: string;
    memberIds?: string[];
    territoryIds?: string[];
  }): Promise<any> {
    try {
      logger.info('Creating team', { teamData });

      const response = await apiService.post(`${BASE_URL}/teams`, teamData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create team', error);
      throw error;
    }
  }

  /**
   * Update team
   */
  async updateTeam(
    teamId: string,
    teamData: {
      teamName?: string;
      teamLeadId?: string;
      memberIds?: string[];
      territoryIds?: string[];
    }
  ): Promise<any> {
    try {
      logger.info('Updating team', { teamId, teamData });

      const response = await apiService.put(`${BASE_URL}/teams/${teamId}`, teamData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update team', error);
      throw error;
    }
  }

  /**
   * Get all teams
   */
  async getTeams(): Promise<any[]> {
    try {
      logger.info('Fetching teams');

      const response = await apiService.get(`${BASE_URL}/teams`);

      const data = response?.data || response;
      return Array.isArray(data) ? data : (data?.list || data?.teams || []);
    } catch (error) {
      logger.error('Failed to fetch teams', error);
      return [];
    }
  }

  /**
   * Get team members with roles
   */
  async getTeamMembers(teamId: string): Promise<any[]> {
    try {
      logger.info('Fetching team members', { teamId });

      const response = await apiService.get(`${BASE_URL}/teams/${teamId}/members`);

      const data = response?.data || response;
      return Array.isArray(data) ? data : (data?.list || data?.members || []);
    } catch (error) {
      logger.error('Failed to fetch team members', error);
      return [];
    }
  }

  // ==================== STATISTICS & ANALYTICS ====================

  /**
   * Get user statistics from dedicated backend endpoint
   * Uses single API call instead of multiple requests
   */
  async getUserStats(filters: UserListFilters = {}): Promise<UserStats> {
    try {
      logger.info('Fetching user statistics', { filters });

      const response = await apiService.get(`${BASE_URL}/stats`);

      // Map backend response to frontend interface
      const data = response?.data || response;

      return {
        total: data.total || 0,
        active: data.active || 0,
        inactive: data.inactive || 0,
        sales_users: data.salesUsers || 0,
        backend_users: data.systemUsers || 0,
        new_this_month: data.newThisMonth || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch user statistics', error);
      return {
        total: 0,
        active: 0,
        inactive: 0,
        sales_users: 0,
        backend_users: 0,
        new_this_month: 0,
      };
    }
  }

  // ==================== DROPDOWN/MASTER DATA SERVICES ====================

  /**
   * Get departments list
   * Note: This endpoint may not exist - you may need to create it in backend or use alternate source
   */
  async getDepartments(): Promise<Array<{ value: string; label: string }>> {
    try {
      logger.info('Fetching departments');

      // Get from user master endpoint
      const response = await apiService.get(`${BASE_URL}/departments`);

      const departments = response?.data?.list || response?.data || response || [];

      return departments.map((dept: any) => ({
        value: dept.value || dept.departmentName,
        label: dept.label || dept.departmentName,
      }));
    } catch (error) {
      logger.error('Failed to fetch departments', error);
      // Fallback to empty array - frontend should handle this gracefully
      return [];
    }
  }

  /**
   * Get designations list
   */
  async getDesignations(): Promise<Array<{ id: string; value: string; label: string }>> {
    try {
      logger.info('Fetching designations');

      const response = await apiService.get(`${BASE_URL}/designations`);

      const designations = response?.data?.list || response?.data || response || [];

      return designations.map((desig: any) => ({
        id: desig.id || desig._id,
        value: desig.value || desig.designationName,
        label: desig.label || desig.designationName,
      }));
    } catch (error) {
      logger.error('Failed to fetch designations', error);
      return [];
    }
  }

  /**
   * Create new designation
   */
  async createDesignation(name: string): Promise<any> {
    try {
      logger.info('Creating designation', { name });

      const response = await apiService.post('/web/master/designations', { name });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create designation', error);
      throw error;
    }
  }

  /**
   * Delete designation
   */
  async deleteDesignation(id: string): Promise<any> {
    try {
      logger.info('Deleting designation', { id });

      const response = await apiService.delete(`/web/master/designations/${id}`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to delete designation', error);
      throw error;
    }
  }

  /**
   * Get reporting managers list by user type
   * Uses the existing user list endpoints
   * @param userType - 'backend' or 'sales'
   * @param search - Optional search term
   */
  private async getReportingManagersByType(
    userType: 'backend' | 'sales',
    search?: string
  ): Promise<Array<{ value: string; label: string }>> {
    try {
      logger.info(`Fetching ${userType} reporting managers`, { search });

      const params: Record<string, string | number> = {
        status: 'active',
        limit: 50,
      };

      if (search?.trim()) {
        params.search = search.trim();
      }

      // Use existing endpoints based on user type
      const endpoint = userType === 'backend' ? `${BASE_URL}/backend-users` : `${BASE_URL}/sales-users`;
      const response = await apiService.get(endpoint, { params });
      const managers = response?.data?.list || response?.data || response || [];

      return managers.map((manager: any) => ({
        value: manager.id || manager.value,
        label: manager.name || manager.label || `${manager.firstName} ${manager.lastName}`.trim(),
      }));
    } catch (error) {
      logger.error(`Failed to fetch ${userType} reporting managers`, error);
      return [];
    }
  }

  /**
   * Get reporting managers list for backend users
   */
  async getReportingManagers(search?: string): Promise<Array<{ value: string; label: string }>> {
    return this.getReportingManagersByType('backend', search);
  }

  /**
   * Get reporting managers list for sales users
   */
  async getSalesReportingManagers(search?: string): Promise<Array<{ value: string; label: string }>> {
    return this.getReportingManagersByType('sales', search);
  }

  // ==================== CONFIGURATION ====================

  /**
   * Get column configuration for user list
   */
  async getColumnConfig(tableIdentifier: string): Promise<any> {
    try {
      logger.info('Fetching column config', { tableIdentifier });

      const response = await apiService.get(`${BASE_URL}/columns/config`, {
        params: { tableIdentifier },
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch column config', error);
      throw error;
    }
  }

  /**
   * Save column configuration for user list
   */
  async saveColumnConfig(dto: {
    tableIdentifier: string;
    columns: Array<{
      key: string;
      visible: boolean;
      order: number;
      width?: number;
    }>;
  }): Promise<any> {
    try {
      logger.info('Saving column config', { tableIdentifier: dto.tableIdentifier });

      const response = await apiService.put(`${BASE_URL}/columns/config`, dto);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to save column config', error);
      throw error;
    }
  }

  /**
   * Get form configuration for user forms
   */
  async getFormConfig(formIdentifier: string): Promise<any> {
    try {
      logger.info('Fetching form config', { formIdentifier });

      const response = await apiService.get(`${BASE_URL}/form/config`, {
        params: { formIdentifier },
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch form config', error);
      throw error;
    }
  }

  /**
   * Save form configuration
   */
  async saveFormConfig(dto: {
    formIdentifier: string;
    fields: Array<{
      key: string;
      visible: boolean;
      required: boolean;
      order: number;
    }>;
  }): Promise<any> {
    try {
      logger.info('Saving form config', { formIdentifier: dto.formIdentifier });

      const response = await apiService.put(`${BASE_URL}/form/config`, dto);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to save form config', error);
      throw error;
    }
  }

  /**
   * Check if mobile number already exists
   * Endpoint: GET /shared/master/user/user-master/lookup/mobile/:mobile
   */
  async checkMobileDuplicate(mobile: string): Promise<{ available: boolean; exists: boolean; message?: string }> {
    try {
      const response = await apiService.get(`/shared/master/user/user-master/lookup/mobile/${mobile}`);
      return response?.data || response || { available: true, exists: false };
    } catch (error) {
      logger.error('Failed to check mobile duplicate', error);
      return { available: true, exists: false };
    }
  }
}

export const userService = new UserService();
export default userService;